import { useLocation, useNavigate } from "react-router-dom";
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  Grid, Chip, LinearProgress, Alert
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HomeIcon from "@mui/icons-material/Home";
import ReplayIcon from "@mui/icons-material/Replay";

export default function ResultsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const result = state?.result;

  if (!result) {
    navigate("/dashboard");
    return null;
  }

  const passed = result.passed;
  const isCertified = result.certified;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Assessment Results
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Card sx={{
          borderRadius: 3, boxShadow: 3, mb: 3,
          border: `3px solid ${passed ? "#4caf50" : "#f44336"}`
        }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Box sx={{ mb: 2 }}>
              {passed
                ? <CheckCircleIcon sx={{ fontSize: 80, color: "#4caf50" }} />
                : <CancelIcon sx={{ fontSize: 80, color: "#f44336" }} />
              }
            </Box>
            <Typography variant="h3" fontWeight={800}
              color={isCertified ? "#1a237e" : passed ? "#2e7d32" : "#c62828"} mb={1}>
              {isCertified ? "CERTIFIED!" : passed ? "PASSED!" : "FAILED"}
            </Typography>
            <Typography variant="h4" fontWeight={700} color="#1a237e" mb={1}>
              {result.score}%
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {result.message}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
              <Chip label={`${result.correct_answers} correct`} color="success" />
              <Chip label={`${result.total_questions - result.correct_answers} incorrect`} color="error" />
              <Chip label={`Pass mark: ${result.pass_mark}%`} color="primary" />
            </Box>

            <Box sx={{ maxWidth: 400, mx: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">Your Score</Typography>
                <Typography variant="body2" fontWeight={600}>{result.score}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={result.score}
                sx={{
                  height: 12, borderRadius: 6,
                  bgcolor: "#ffcdd2",
                  "& .MuiLinearProgress-bar": { bgcolor: passed ? "#4caf50" : "#f44336" }
                }} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Pass mark: {result.pass_mark}%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {result.critical_failed && (
          <Alert severity="error" sx={{ mb: 3 }}>
            A critical question was answered incorrectly. This is a mandatory pass requirement.
          </Alert>
        )}

        {result.topic_breakdown.length > 0 && (
          <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Performance by Topic</Typography>
              {result.topic_breakdown.map((topic, i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>{topic.topic_name}</Typography>
                    <Typography variant="body2">
                      {topic.correct_count}/{topic.questions_count} ({topic.score_percent}%)
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={topic.score_percent}
                    sx={{
                      height: 8, borderRadius: 4,
                      "& .MuiLinearProgress-bar": {
                        bgcolor: topic.score_percent >= 80 ? "#4caf50" : topic.score_percent >= 60 ? "#ff9800" : "#f44336"
                      }
                    }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="outlined" size="large" startIcon={<HomeIcon />}
            onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
          {!passed && (
            <Button variant="contained" size="large" startIcon={<ReplayIcon />}
              onClick={() => navigate(-2)}
              sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
              Try Again
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
